import type React from 'react';
import { useState } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Close from '@mui/icons-material/Close';
import { DateCalendarBase } from 'components/date-calendar/DateCalendarBase';

interface ExpiryDateCalendarModalProps {
  currentExpiryDate: Dayjs;
  isOpen: boolean;
  onResolve: (selectedDate: Dayjs | null) => void;
  onCancel: () => void;
}

export const ExpiryDateCalendarModal: React.FC<ExpiryDateCalendarModalProps> = ({ currentExpiryDate, isOpen, onResolve, onCancel }) => {
  const [expiryDate, setExpiryDate] = useState(currentExpiryDate.isValid() ? currentExpiryDate : dayjs());
  const { t } = useTranslation();

  const handleFixedExpiryDate = (days: number) => {
    setExpiryDate(dayjs().add(days, 'day'));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      PaperProps={{ sx: { padding: '16px 24px', borderRadius: 6 } }}
      data-testid="expiry-date-calendar-modal"
    >
      <Stack direction="row" justifyContent="space-between" sx={{ paddingBottom: 2 }}>
        <Typography variant="h6">{t('share_document_v3.calendar_modal.title')}</Typography>
        <IconButton aria-label="close" onClick={onCancel} sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Stack>
      <DateCalendarBase value={expiryDate} onChange={setExpiryDate} disablePast />
      <Stack direction="row" alignItems="center" gap={1} mt={2}>
        <Button variant="contained" color="secondary" size="small" onClick={() => handleFixedExpiryDate(30)}>
          {t('share_document_v3.calendar_modal.30_days_label')}
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => handleFixedExpiryDate(60)}>
          {t('share_document_v3.calendar_modal.60_days_label')}
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => handleFixedExpiryDate(90)}>
          {t('share_document_v3.calendar_modal.90_days_label')}
        </Button>
      </Stack>
      <DialogActions>
        <Button variant="text" color="primary" onClick={onCancel}>
          {t('button.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            onResolve(expiryDate);
            onCancel();
          }}
        >
          {t('button.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
